import * as React from 'react';
import RoleUtil from 'src/utils/RoleUtil';
import Role from 'src/consts/Role';
import { CheckCircleTwoTone, CloseCircleTwoTone, ExportOutlined, InfoCircleOutlined, RedoOutlined } from '@ant-design/icons';
import Routes from 'src/consts/Routes';
import {
    Button,
    Card,
    Col,
    Row,
    Descriptions,
    Result,
    Tooltip,
    Skeleton, Tag, Spin, Collapse, Typography, Space,
    Divider,
    ConfigProvider
} from 'antd';

import BinDTO from 'src/models/BinDTO';
import BinInfoDTO from 'src/models/BinInfoDTO';
import BinApiService from 'src/api/BinApiService';
import Canvas from '../pages/dashboard/HomeScreen/Canvas';
import OfflineBinReportDTO from '../models/OfflineBinReportDTO';
import HeaterMode from 'src/consts/HeaterMode';
import { HistoryState } from '../pages/dashboard/BinStatusPage/BinStatsPage';
import { LogViewer } from '../pages/dashboard/BinStatusPage/LogViewer';
import { binConfiguredWithHeaters, downloadAsciiBinState } from '../pages/dashboard/BinStatusPage/BinVisualThree';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import PingAndBinDTO from 'src/models/PingAndBinDTO';
import { useHistory, useLocation } from 'react-router';
import { useCallback, useEffect, useRef } from 'react';
import { binDBKeys } from 'src/pages/binOverview/BinCommander';
import { useBinStateFromDevice } from 'src/queries/useBinStateFromDevice';
import { DeviceLogRangeForm } from '../pages/dashboard/BinStatusPage/DeviceLogsRangeForm';
import { LogType, viewBinStateLogsByRange, viewGrainStateLogsByRange, viewRunLogsByRange } from 'src/pages/shared/downloadLogsByRange';
import dayjs from 'dayjs';
import { formatBool, formatNumber } from '../pages/dashboard/BinStatusPage/HeaterControls';
import { BinOfflineIndicator, useExtraOfflineInfoContext } from '../pages/dashboard/BinOfflineIndicator';
import { BinDTOContext, useBinDTOContext } from 'src/queries/BinDTOContext';
import { BinInfoContext, useBinInfoContext } from 'src/queries/BinInfoContext';
import { DashboardCardShivversPremier } from 'src/pages/features/Premier/DashboardCardShivversPremier';
import AutomationType from 'src/consts/AutomationType';
import PremierPlusOverview from 'src/components/Premier/PremierPlus/PremierPlusOverview';
import { calculateFanStatus, DerivedFanStatus } from 'src/pages/features/shared';
import { ThemeProvider } from '@tanstack/react-query-devtools/build/lib/theme';
import { formatEstimatedBuWithComma } from 'src/pages/dashboard/BinStatusPage/BinStatusCard';
import { Co2Badge } from './Co2Badge';
import { BinProductDescriptionTag } from './BinProductDescriptionTag';
import { formatCompressorStatusReact, areValvesPermanentlyClosed, formatValvesStatus, OnStatus, OffStatus, PausedStatus } from 'src/utils/formatting';
import { BIN_DETAILS_TOOLTIP_TEXT, BinDetailsButton } from './BinDetailsButton';
import { RefreshButtonNew } from './RefreshButtonNew';
const { Item } = Descriptions;
const { Panel } = Collapse;

// Debounced values
import {
  useWindowSize,
  useWindowWidth,
  useWindowHeight,
} from '@react-hook/window-size'
import { BinDetailsStyledCard } from './BinDetailsStyledCard';

export const getUserTimezoneOffset = (): number => {
    return new Date().getTimezoneOffset();
}

// interface State {
//     refreshing: boolean,
// }

interface Props {
    bin: BinInfoDTO;
    offlineReport: OfflineBinReportDTO | null;
    id: number;
    targetValue: number | null;
    binLength: number;
    isLoading(val: boolean, i: number): void;
}

type QueryTypes = Parameters<typeof useQuery>[2];

interface SnapShotReturn {
    bin: BinDTO | null,
    online: boolean,
    devicePing: boolean | null,
    snapshotOld: boolean,
    snapshotError: boolean,
}

export const isAnyHeaterOn = (binDTO: BinDTO): boolean | null => {
    const maybeFans = binDTO?.fans;
    if (maybeFans == null) {
        return null;
    }

    const fans = maybeFans;

    for (const fan of fans) {
        return fan.isHeaterOn;
    }
    return null;
}

const transformPingAndBinDTO = (response: PingAndBinDTO): SnapShotReturn => {
    let transformed: SnapShotReturn = {
        bin: response.binDTO,
        online: true,
        devicePing: null,
        snapshotOld: false,
        snapshotError: false,
    };
    try {
        if (response.binDTO !== null) {
            let online = true;
            const date = dayjs(new Date());
            const lastUpdateTime = dayjs(response.binDTO.captureTimeUtc);
            const snapShotAgeMinutes = date.diff(lastUpdateTime, 'minutes');
            if (snapShotAgeMinutes > 30) {
                online = false;
                transformed.snapshotOld = true;
            }
            transformed.devicePing = response.ping;
            transformed.online = online || response.ping;
        }
        else {
            transformed.snapshotError = true;
            transformed.devicePing = response.ping;
            transformed.online = response.ping;
        }
    } catch (err) {
        transformed.online = false;
    }
    return transformed;

}

export const deviceQueryKeys = {
    all: ["Devices"] as const,
    device: (azureDeviceID: string) => [...deviceQueryKeys.all, azureDeviceID] as const,
    devicePing: (azureDeviceID: string) => [...deviceQueryKeys.device(azureDeviceID), "ping"] as const,
    stateFromAzure: (azureDeviceID: string) => [...deviceQueryKeys.device(azureDeviceID), "AzureState"] as const,
    stateFromDevice: (azureDeviceID: string) => [...deviceQueryKeys.device(azureDeviceID), "State"] as const,
    offlineReport: (azureDeviceID: string) => [...deviceQueryKeys.device(azureDeviceID), "oflineReport"] as const,
    everyWareCloud: (azureDeviceID: string) => [...deviceQueryKeys.device(azureDeviceID), "EC"] as const,
};

export const useDevicePingAndReport = (deviceId: string, userTimeZoneOffset: number, options: QueryTypes = {}) => {
    const query = useQuery(deviceQueryKeys.devicePing(deviceId), async (q) => {
        return await BinApiService.pingDeviceAndGetBinDTOFromAzure(deviceId!, userTimeZoneOffset, q.signal)
    }, {
        enabled: options?.enabled,
        onError: () => options?.onError,
        select: transformPingAndBinDTO,
    });
    return query;
};

export const useOfflineReport = (azureDeviceID: string | null | undefined, enabled?: boolean) => {

    return useQuery(deviceQueryKeys.offlineReport(azureDeviceID!), async (q) => {
        if (azureDeviceID == null) {
            throw new Error("No Azure device supplied");
        }
        const report = await BinApiService.getOfflineConnectionReport(azureDeviceID!, q.signal);
        return report;
    }, {
        enabled: enabled,
    });
};

export const useEcDeviceQuery = (azureDeviceId: string | null | undefined, enabled: boolean) => {
    const query = useQuery(deviceQueryKeys.everyWareCloud(azureDeviceId!), async (q) => {
        if (azureDeviceId == null) {
            throw new Error("No azure device id supplied");
        }
        const res = await BinApiService.getECDevice(azureDeviceId, q.signal);
        return res;
    }, {
        enabled,
    });
    return query;
};

export enum ECConnectionStatus {
    MISSING = "MISSING",
    CONNECTED = "CONNECTED",
    DISCONNECTED = "DISCONNECTED",
}

interface ConnectionStatus {
    online: boolean,
    recentlyDisconnected?: boolean;
}

const ecDeviceIsOnline = (status: ECConnectionStatus | null | undefined): ConnectionStatus | null => {
    if (status == null) { return null }
    if (status === ECConnectionStatus.CONNECTED) { return { online: true }; }
    if (status === ECConnectionStatus.DISCONNECTED) { return { online: false, recentlyDisconnected: true }; }
    if (status === ECConnectionStatus.MISSING) { return { online: false, recentlyDisconnected: false }; }
    return null;
}

interface ModeItemTextProps {
    isPaused: boolean,
    pauseText: React.JSX.Element | string,
    operationText: string,
}
const pauseStyle: React.CSSProperties = {color: "red", fontWeight: "bold"};
const ModeItemText = (props: ModeItemTextProps) => {
    return <span style={props.isPaused ? pauseStyle: undefined}>
        <span>{props.operationText}</span>
        {props.isPaused && <>
            <br />
            <span> {props.pauseText}</span>
            </>
        }
    </span>
}



export const FanStatusText = (props: {binDTO: BinDTO | null | undefined}) => {
    const fanStatus = calculateFanStatus(props.binDTO);
    if (fanStatus == null) {
        return "--";
    }
    if (fanStatus === DerivedFanStatus.On) {
        return <OnStatus />;
    }
    else if (fanStatus === DerivedFanStatus.Off) {
        return <OffStatus />;
    }
    else {
        return <PausedStatus />;
    }
}

const binDetailsCardBreakpionts = (width: number): number => {
    if (width > 1500) {
        return 8;
    }
    if (width > 1100 && width <= 1500) {
        return 12;
    }
    return 24;
}

const BinDetails = (props: Props) => {
    const isLegacyPremier = props.bin?.isLegacyPremier === true;
    const queryClient = useQueryClient();
    const extraOfflineInfoContext = useExtraOfflineInfoContext();

    const showExtraOfflineInfo = extraOfflineInfoContext.showExtraOfflineInfo;
    const width = useWindowWidth();
    const binCardSpan = binDetailsCardBreakpionts(width);

    let binLength = props.binLength;
    const binInfo = props.bin;
    const azureDeviceId = binInfo?.deviceId;
    const binXl: number = 8;
    const binLg: number = 12;

    const userTimeZoneOffset = getUserTimezoneOffset();

    const history = useHistory<HistoryState>();
    const location = useLocation<HistoryState>();

    const azurePingAndDTOQuery = useDevicePingAndReport(props.bin?.deviceId!, userTimeZoneOffset, {
        enabled: (azureDeviceId?.length ?? 0) > 0 && props.bin?.isLegacyPremier === false && props.bin?.automationType !== AutomationType.PremierPlus,
    });

    const deviceBinDTOQuery = useBinStateFromDevice(
        {
            azureDeviceID: props?.bin?.deviceId!,
            binId: props?.bin?.id,
            userTimeZoneOffset,
        },
        { enabled: false });

    let bin = azurePingAndDTOQuery.data?.bin;

    const deviceNewer = (): boolean => {
        const onDeviceLastUpdated = deviceBinDTOQuery.data?.captureTimeUtc;
        const azureCaptureTime = azurePingAndDTOQuery.data?.bin?.captureTimeUtc;
        if (azureCaptureTime == null || onDeviceLastUpdated == null) {
            return false;
        }
        return onDeviceLastUpdated >= azureCaptureTime;

    };

    // Show the device binstate if it is newer only if the refresh button has been pressed
    if (deviceNewer() && deviceBinDTOQuery.isFetchedAfterMount) {
        // console.log(`using device data: ${binInfo.deviceId}`);
        bin = deviceBinDTOQuery.data;
    }
    else {
        // console.log(`using azure data: ${binInfo.deviceId}`);
        bin = azurePingAndDTOQuery.data?.bin;
    }

    const shouldGetMoreOfflineReasons = azurePingAndDTOQuery.data?.online === false && props.bin?.isLegacyPremier === false;
    const offlineReportQuery = useOfflineReport(props.bin?.deviceId, shouldGetMoreOfflineReasons);
    const { data: offlineReport } = offlineReportQuery;

    const createBinDetails = (online: boolean) => {
        if (azurePingAndDTOQuery.data?.bin == null) {
            console.warn(`${binInfo?.deviceId} Bin data not supplied when creating bin details`);
        }
        const devicePing = azurePingAndDTOQuery.data?.devicePing;
        const isAdmin = RoleUtil.currentUserIsAdmin();

        let winWidth = window.innerWidth;
        let op;

        const renderCanvas = () => {

            if (isAdmin && !online && showExtraOfflineInfo) {
                return false;
            }
            return true;
        }

        let tagColor = '#ccbe8d';
        switch (bin?.operatingMode) {
            case 0:
                op = "Idle";
                break;
            case 1:
                op = 'Fill & Aerate';
                break;
            case 2:
                op = 'Pre Dry';
                break;
            case 3:
                op = 'Dry Mode';
                break;
            case 31:
                op = 'Top Dry';
                break;
            case 20:
                op = "User Control";
                break;
            case 101:
                op = 'Self Check';
                break;
            case 100:
                op = 'Fail Safe';
                tagColor = '#FF0000';
                break;
            case 4:
                op = 'Aeration';
                break;
            case 5:
                op = 'Unload';
                break;
            default:
                op = `In Empty/Idle (${bin?.operatingMode ?? ""})`;
                break;
        }

        if (bin?.isPaused) {
            tagColor = '#FBD900';
        }

        const pauseText = `Paused: ${bin?.reasonForPause}`;

        let canvasDem = (winWidth >= 992 ? 320 : (winWidth >= 768 ? 300 : 240)) / 2;
        let ifXl = winWidth >= 1200 ? window.innerWidth * .6 : (winWidth >= 992 ? window.innerWidth * .8 : window.innerWidth);

        const lastUpdatedCssProps: React.CSSProperties = {
            fontWeight: 500,
            color: "white",
            fontSize: 11,
        };
        if (azurePingAndDTOQuery.data?.snapshotOld) {
            lastUpdatedCssProps.background = '#FF0000';
        }

        const showStigCard = !isLegacyPremier && (binInfo.automationType === AutomationType.DriStack || binInfo.automationType === AutomationType.AutoBin );
        const showLegacyPremierCard = isLegacyPremier;
        const showPremierPlusCard = !isLegacyPremier && binInfo.automationType === AutomationType.PremierPlus;
        if ([showStigCard, showLegacyPremierCard, showPremierPlusCard].every(t => t === false)) {
            console.warn(`No bin card to render for ${binInfo.name}, all cards are false`, "bin automation type", binInfo.automationType);
        }

        return (
            <BinInfoContext.Provider value={binInfo}>
                <BinDTOContext.Provider value={deviceNewer() ? deviceBinDTOQuery?.data : azurePingAndDTOQuery?.data?.bin}>
                    {showLegacyPremierCard && <DashboardCardShivversPremier />}
                    {showStigCard && <BinDetailsStyledCard key="card"
                        title={<div className="bin-detail-card-title" style={{
                            //lineHeight: 0.9,
                            }}><Row justify="start" align="middle">
                            <Col>
                                <Space direction='horizontal' wrap size={"small"}>
                                    <span>{binInfo.name}</span>
                                    <BinProductDescriptionTag binDTO={bin} />
                                </Space>
                            </Col>
                        </Row>
                        <Row gutter={[4, 0]}>
                                                        <Col>
                                {
                                    devicePing == null ?
                                        <Tooltip placement="bottom" title="Pinging bin." trigger="hover"><Spin size="small" /></Tooltip>
                                        : devicePing ?
                                            <Tooltip placement="bottom" title="Connected to bin." trigger="hover">
                                                <CheckCircleTwoTone twoToneColor="#52c41a" /></Tooltip>
                                            :
                                            <Tooltip placement="bottom" title="Failed to connect to bin. If the latest update was less than 10 minutes ago then
                                                this is likely a transient issue."
                                                trigger="hover"><CloseCircleTwoTone twoToneColor="#eb2f96" /></Tooltip>
                                }
                            </Col>
                                                        <Col>
                                {bin !== null ?
                                    <Space direction='horizontal' size="small" wrap>
                                        <Tooltip placement="bottom" title="This should update every 5 minutes." trigger="hover">
                                            <span style={lastUpdatedCssProps}>
                                                {'Last updated: ' + dayjs(bin?.captureTimeUtc).format('MM/DD/YYYY h:mm a')}
                                            </span>
                                        </Tooltip>
                                        {/* <BinOfflineIndicator binDTO={deviceNewer() ? deviceBinDTOQuery?.data : azurePingAndDTOQuery?.data?.bin} /> */}
                                    </Space>
                                    : null}
                            </Col>

                        </Row>
                        </div>
                        }

                        extra={<Row gutter={8} justify="end">
                            <Col>
                                <RefreshButtonNew loading={deviceBinDTOQuery.isFetching} disabled={deviceBinDTOQuery.isFetching}
                                    onClick={(event: any) => {
                                        event.stopPropagation();
                                        deviceBinDTOQuery.refetch();
                                    }} />
                            </Col>
                            <Col>
                                <BinDetailsButton
                                    onClick={() => {
                                        queryClient.setQueriesData(binDBKeys.binInfo(binInfo?.id), (oldData: any) => {
                                            if (binInfo?.id === undefined) {
                                                return oldData;
                                            }

                                            return binInfo;
                                        });
                                        const binStatsRoute = `${Routes.BIN_STATS}/${binInfo.id}`;

                                        history.push(binStatsRoute, {
                                            binInfo: binInfo,
                                        })
                                    }}>
                                    </BinDetailsButton>
                            </Col>
                        </Row>
                        }>
                        <Row justify="center" gutter={[0, 0]} align={"stretch"}>
                            <Col xs={24} sm={12}>
                            
                            {renderCanvas() && <BinOverview2dGraphicContainer canvasDem={canvasDem} />}
                            {!renderCanvas() && <OfflineInfoContent offlineReport={offlineReport!} />}
                            </Col>
                            <Col xs={24} sm={12}>
                            <Descriptions
                                bordered={true}
                                column={1}
                                size="small"
                                labelStyle={{width: 88,
                                    fontSize: 11,
                                }}
                                contentStyle={{
                                    fontSize: 11,
                                    paddingRight: 2,
                                }}
                                style={{ width: '100%'}}
                            >
                                {bin?.automationType == AutomationType.DriStack && <Item label="Mode"><ModeItemText operationText={op} isPaused={bin?.isPaused === true} pauseText={pauseText} /></Item>}
                                <Item label="Grain Type">{bin?.grain?.grainType}</Item>
                                <Item label="Fan"><FanStatusText binDTO={bin} /></Item>
                                {binConfiguredWithHeaters(bin) && <Item label="Heater">{formatBool(isAnyHeaterOn(bin!),
                                { true: <OnStatus />, false: <OffStatus />, null: '--', bold: false })} </Item>}
                                {bin?.compressorState != null && <Item label="Compressor">
                                    {formatCompressorStatusReact(bin)}
                                    </Item>}
                                {(bin?.stacks?.length ?? 0) > 0 && <Item label="Valves">
                                    {formatValvesStatus(bin)}
                                </Item>}
                                {(bin?.hardwareYear ?? 0) > 2021 && (bin?.innerCO2 != null || bin?.outerCO2 != null) && <Item label="CO2">
                                    <Space wrap direction='horizontal' size="small">
                                        {formatNumber(bin?.cO2Level, { filler: "--", decimalPlaces: 0, suffix: " ppm"})}
                                        <Co2Badge co2={bin.cO2Level} />
                                    </Space>
                                    </Item>}
                                {(bin?.innerSonar != null || bin?.outerSonar != null) && <Item label={<>
                                    <Space direction='horizontal' wrap>
                                        <Tooltip title="Fill level is based on the headspace sensor" overlayStyle={{ minWidth: "300px" }} overlayInnerStyle={{ textAlign: 'center' }}>
                                            <span>Fill Level</span>
                                        </Tooltip>
                                    </Space>
                                </>}
                                >{formatNumber(bin?.grain?.percentFullCalculated, { decimalPlaces: 1, suffix: "%" })}
                                <span style={{fontSize: "0.9em"}}>&nbsp;({formatEstimatedBuWithComma(bin)})</span>

                                </Item>
                                }
                                {bin?.ambientAir != null && <Item label="Ambient">
                                    {formatNumber(bin?.ambientAir.mc, {decimalPlaces: 1, suffix: "% EMC"})}
                                    <ConfigProvider theme={{
                                        components: {
                                            Divider: {
                                                verticalMarginInline: "4px",
                                            }
                                        }
                                    }}>
                                        <Divider type="vertical" />
                                    </ConfigProvider>
                                    {formatNumber(bin?.ambientAir.temp, {decimalPlaces: 0, suffix: "°F"})}
                                    </Item>}

                                {/* {binConfiguredWithHeaters(bin) && <Item
                            label="Set Point">{heaterSetPointText()}
                        </Item>} */}

                            </Descriptions>

                                
                            {/* {<Row justify='center' style={{paddingTop: 8}}>
                                <Col xs={24}>
                                    <Button block style={{ overflow: 'hidden'}} onClick={() =>
                                        downloadAsciiBinState(bin!)}>Download Diagnostic File</Button>
                                </Col>
                            </Row>
                            } */}
                            </Col>
                            

                        </Row>

                    </BinDetailsStyledCard>}

                    {showPremierPlusCard && 
                        <PremierPlusOverview 
                        layout={'horizontal'}
                        binId={binInfo.id}
                        binName={binInfo.name ?? "Unknown"} 
                        deviceId={binInfo.deviceId ?? 'N/A'}
                        binInfo={binInfo}
                        />
                    }
                </BinDTOContext.Provider>
            </BinInfoContext.Provider>
        );
    };

    const dristackModuleOnline = azurePingAndDTOQuery.data?.online;

    const showLoadingIndicator = azurePingAndDTOQuery.isInitialLoading || azurePingAndDTOQuery.isFetching;

    const binCardRef = useRef<HTMLDivElement>(null);

    useEffect(function ScrollCardIntoView() {
        if (binCardRef.current == null) {
            return;
        }
        const fromPrior = location.state?.binInfo?.id === binInfo.id;
        if (fromPrior) {
            binCardRef.current?.scrollIntoView({ behavior: "auto", block: 'center' });
        }
    }, [location.state?.binInfo?.id, binCardRef.current == null]);



    return (
        <Col ref={binCardRef} key={props.id + 5} span={binCardSpan} style={{ paddingBottom: 20 }}>
            <Skeleton loading={showLoadingIndicator} active={true} paragraph={{ rows: 10 }}> {
                createBinDetails(dristackModuleOnline!)
            } </Skeleton>
        </Col >
    );
}

interface BinOverview2dGraphicContainerProps {
    canvasDem: number;
}
export const BinOverview2dGraphicContainer = (props: BinOverview2dGraphicContainerProps) => {
    const queryClient = useQueryClient();
    const history = useHistory();

    const binInfo = useBinInfoContext();
    const binDTOContext = useBinDTOContext();
    const binDTO = binDTOContext.binDTO;

    return <>
        {binDTO && <Tooltip placement="bottom" title={BIN_DETAILS_TOOLTIP_TEXT} trigger="hover"><a onClick={() => {
            queryClient.setQueriesData(binDBKeys.binInfo(binInfo?.id), (oldData: any) => {
                if (binInfo?.id === undefined) {
                    return oldData;
                }

                return binInfo;
            });
            const binStatsRoute = `${Routes.BIN_STATS}/${binInfo.id}`;

            history.push(binStatsRoute, {
                binInfo: binInfo,
            });
        }}
        >
            <Canvas width={props.canvasDem} height={props.canvasDem} binData={binDTOContext.binDTO!} />
        </a></Tooltip>}

    </>;
}

interface OfflineInfoContentProps {
    offlineReport: OfflineBinReportDTO | null | undefined;

}

export const OfflineInfoContent = (props: OfflineInfoContentProps) => {
    const binInfo = useBinInfoContext();
    const offlineReport = props.offlineReport;
    const ecDeviceQuery = useEcDeviceQuery(binInfo.deviceId, true);
    const ecDeviceStatus = ecDeviceIsOnline(ecDeviceQuery.data?.context?.connection?.status as ECConnectionStatus);

    // if the DB Bin.deviceId is ever != dristackmodule twin properties.desired.binName there will be issues in getting logs from the Azure folder
    const { handleLogClickSubmit } = useBinStateLogFunctions(binInfo.deviceId!);
    const deviceTwinLastActivityTimeDate = dayjs(offlineReport?.reportedPropertiesLastActivityTime);

    const isAdmin = RoleUtil.currentUserIsAdmin();
    let title = "Bin Error";
    if (ecDeviceStatus?.online === false) {
        title = "Bin Offline";
    }
    else if (offlineReport?.connectionState === "Disconnected") {
        title = "Dri-Stack Not Running";
    }
    return <>
        <Result status="warning" style={{ paddingTop: 20, paddingBottom: 20 }} title={title} />
        <b><u>Last Reported Info:</u></b> <br />
        <b>Mode:</b> {offlineReport?.currentMode}<br />
        <b>Routine:</b> {offlineReport?.currentRoutine}<br />
        <b>Reported Properties Activity:</b> {deviceTwinLastActivityTimeDate.isValid() ? deviceTwinLastActivityTimeDate?.format("L LTS") : undefined}<br />
        <b>Module Twin Activity:</b> {dayjs(offlineReport?.moduleTwinLastActivityTime)?.format("L LTS")}<br />
        <b>ConnectionState:</b> {offlineReport?.connectionState}<br />
        <b>Message:</b> {offlineReport?.message} <br />
        {isAdmin && <><b>EC Device Display Name:</b> {ecDeviceQuery.data?.context?.displayName} <br /></>}
        <b>Device Online Status:</b> {ecDeviceQuery.data?.context?.connection?.status} <br />
        <br /> <br />
        <div style={{ display: isAdmin ? 'block' : 'none' }} >
            <b>Logs</b><br />
            {isAdmin &&
                <Col span={64} style={{ paddingTop: 4, paddingLeft: 0, paddingRight: 0 }}>
                    <Collapse>
                        <Panel header="Bin State by Date" key="1">
                            <DeviceLogRangeForm onSubmit={handleLogClickSubmit('BinState')} />
                        </Panel>
                        <Panel header="Grain State by Date" key="2">
                            <DeviceLogRangeForm onSubmit={handleLogClickSubmit('GrainState')} />
                        </Panel>
                        <Panel header="Run Logs by Date" key="3">
                            <DeviceLogRangeForm onSubmit={handleLogClickSubmit('Run')} />
                        </Panel>
                    </Collapse>
                </Col>}
        </div>
        {isAdmin && (ecDeviceStatus?.online || offlineReport?.connectionState === 'Connected') && <LogViewer deviceId={binInfo.deviceId!} />}
    </>
}

export const useBinStateLogFunctions = (deviceId: string) => {

    const handleLogClickSubmit = useCallback((type: LogType) => (dateStart: string, dateEnd: string) => {
        const clientFolder = deviceId;
        switch (type) {
            case "BinState":
                viewBinStateLogsByRange(clientFolder!, dateStart, dateEnd);
                break;
            case "GrainState":
                viewGrainStateLogsByRange(clientFolder!, dateStart, dateEnd);
                break;
            case "Run":
                viewRunLogsByRange(clientFolder!, dateStart, dateEnd);
                break;
        }
    }, [deviceId]);

    return React.useMemo(() => {
        return {
            handleLogClickSubmit: handleLogClickSubmit,
        }
    }, [handleLogClickSubmit, deviceId]);
}

export default BinDetails;
